import { action, transaction, observable } from 'mobx'
import * as defaultConst from '../../../constants/default_constants'
import _map from 'lodash/map'
import _uniq from 'lodash/uniq'

const TYPE_MAPPING = {
  movie: 'FILM'
}

export default (store) => class OmdbStore {
  constructor (view) {
    this.view = view
  }

  @observable data = false

  // AD HOC function to save person from external source
  savePerson (personName) {
    const data = {
      name: [{
        lang: 'en',
        value: personName.trim()
      }],
      desc: [],
      birth_year: ''
    }
    return store.requester.call('/persons', 'post', data)
  }

  @action
  cancelOMDBload () {
    this.data = false
  }

  findAttrsAttr (platform, attrname, lang) {
    const attrs = this.view.record.get('attrs')
    const found = attrs.find((i) => {
      return i.platform === platform && i.name === attrname &&
        (i.lang === lang || (!i.lang && !lang))
    })
    return found
  }

  // Downloads data from OMDB API and saves result to state.cv.OMDBdata
  @action
  getDataFromOMDBAPI ({ imdb_id, callbackFn }) {
    const entity = this.view.record
    const self = this
    const prepareResponse = (response) => {
      if (!response || response.data.Response === 'False') {
        throw (response.data.Error === 'Error getting data.' && 'Film not found in the OMDb database.') || response.data.Error
      }
      return response.data
    }
    const url = `https://www.omdbapi.com/?i=${imdb_id}&r=json&apikey=${Conf.omdbApiKey}`

    store.requester.callExternalRes({ url: `${url}&plot=short`, method: 'get' })
      .then(prepareResponse)
      .catch(errorMessage => {
        store.addMessage(store.__(errorMessage), 'error', 4000)
        return false
      })
      .then((OMDBdata) => {
        this.data = OMDBdata && (typeof OMDBdata === 'object') ? OMDBdata : {}

        if (callbackFn && {}.toString.call(callbackFn) === '[object Function]') {
          callbackFn(OMDBdata)
        }

        // Check if date is passed, if date is passed right
        // and if so, transfer it into right format YYYY-MM-DD
        const rDate = new Date(this.data.Released)
        this.data.Released = rDate && !isNaN(rDate.getTime())
          ? ['' + rDate.getUTCFullYear(),
            ('0' + (rDate.getUTCMonth() + 1)).slice(-2),
            ('0' + rDate.getUTCDate()).slice(-2)
          ].join('-')
          : null

        // Runtime should be only number. Remove all text like (min)
        this.data.Runtime = this.data.Runtime ? parseInt(this.data.Runtime) : null

        const cpl = defaultConst.CORS_PROXY_SERVERS
        const ratingListByImdbId = (imdb_id, cb) => {
          if (!cpl || cpl.length <= 0) {
            return
          }
          var x = new XMLHttpRequest()
          x.open('GET', `${cpl[0]}https://www.imdb.com/title/${imdb_id}/parentalguide`)
          x.onload = x.onerror = () => {
            if (!x.responseText) { // (Math.random() * (100 - 0) + 0) >= 90  (90% change that proxy wont work)
              if (cpl.shift() && cpl.length > 0) {
                ratingListByImdbId(imdb_id, cb) // repeat action on new list of working proxies
              }
            } else {
              const doc = new DOMParser().parseFromString(x.responseText, 'text/html')
              const ratinglist = doc.querySelectorAll('#certifications-list .ipl-inline-list__item a')
              const ratingArray = []
              ratinglist.forEach(e => {
                const qp = new URL(`https://www.imdb.com${e.getAttribute('href')}`).searchParams.get('certificates')
                ratingArray.push({
                  name: qp.split(':')[1],
                  region: qp.split(':')[0]
                })
              })
              ratingArray ? cb(ratingArray) : cb(null)
            }
          }
          x.send('')
        }

        ratingListByImdbId(imdb_id, rlist => {
          const originalRating = new Set()
          rlist.forEach(r => originalRating.add(`${r.name}:${r.region}`))
          this.data = (typeof this.data === 'object' ? this.data : {})
          this.data.Rated = [...originalRating].join(',')
        })

        // Send another request with parameter plot=full
        return store.requester.callExternalRes({ url: `${url}&plot=full`, method: 'get' })
      })
      .then(prepareResponse)
      .catch(() => { return false })
      .then((OMDBdata) => {
        if (this.data && OMDBdata.Plot && OMDBdata.Plot !== 'N/A') {
          this.data.PlotFull = OMDBdata.Plot
        }
      })
      .catch(store.onRequesterError.bind(store))
      .catch(store.onRequesterError.bind(store))
  }

  // called from modal in film view
  @action
  saveOMDBdata (overwriteExistingValues) {
    const self = this
    const entity = this.view.record
    const OMDBdata = this.data

    function _existsOMDBAttribute (OMDBAttrName) {
      return (OMDBdata[OMDBAttrName] && OMDBdata[OMDBAttrName] !== 'N/A')
    }

    // Inserts tag type value to currentView entity tag type property.
    // Create code if code for inserted value doesn't exist
    // In the case code is automatically generated, both text and value are lowercase
    function _overwriteTags (tagName, enumParent, OMDBAttrName) {
      if (!overwriteExistingValues && self.view.record.get(tagName).length > 0) {
        return
      }
      const vals = []

      function _add (val) {
        // look into existing roles if there is already same item
        const existingItem = self.view.record.get(tagName).find((i) => (i.enum_value === val))
        const added = existingItem || {
          enum_value: val
        }
        vals.push(added)
      }

      if (_existsOMDBAttribute(OMDBAttrName)) {
        OMDBdata[OMDBAttrName].toLowerCase().split(',').map((valueToAdd) => {
          const val = valueToAdd.trim()

          // seek and destroy alternative countries, because of duplicates
          let altCode = null
          if (val && tagName === 'origcountries') {
            const countryAlter = store.options.get('countriesAlternative')
              .find(i => i.name.toLowerCase() === val.toLowerCase())
            altCode = countryAlter && countryAlter.code_ISO
          }

          const foundCode = altCode || store.isTextOptionAvailable(val, enumParent)
          if (!foundCode) {
            // saves not found enum item to database
            self.saveEnumOMDB(enumParent, 'en', val, val)
            store.addEnumOption({ text: val, value: val, parent: enumParent, lang: 'en' }, enumParent)
            _add(val)
          } else {
            _add(altCode || foundCode.value)
          }
        })
      }
      self.view.record.set(tagName, vals)
    }

    function _writeAttr (platform, attrName, OMDBAttrName, lang = null) {
      if (_existsOMDBAttribute(OMDBAttrName)) {
        if (!overwriteExistingValues) {
          const existing = self.findAttrsAttr(platform, attrName, lang)
          if (existing && existing.value) {
            return // don't overwrite
          }
        }
        store.changeAttrArrayValue('attrs', { platform, name: attrName, lang }, OMDBdata[OMDBAttrName])
      }
    }

    function _getPersonList (OMDBAttrName, attr) {
      const parts = _existsOMDBAttribute(OMDBAttrName) ? OMDBdata[OMDBAttrName].split(',') : []
      const final = _map(parts, (i) => {
        return i.replace(/\s*\(.*?\)\s*/g, '').trim()
      })
      return _uniq(final)
    }

    function _tryAddPerson2Roles (personId, roleId, roles) {
      // look into existing roles if there is already same item
      const existingRole = self.view.record.get('roles').find((i) => (
        i.role_id === roleId && i.person_id === personId
      ))
      const role = existingRole || {
        person_id: personId,
        role_id: roleId
      }
      roles.push(role)
    }

    function _writePersons () {
      if (!overwriteExistingValues && self.view.record.get('roles').length > 0) {
        return
      }
      const roles = []
      const persons = {
        actor: _getPersonList('Actors', 'actor'),
        director: _getPersonList('Director', 'director'),
        writer: _getPersonList('Writer', 'writer')
      }
      const promises = []

      _map(persons, (plist, role) => {
        _map(plist, (personName) => {
          const existingPerson = store.options.get('persons').find(i => (i.name === personName))
          if (!existingPerson) {
            const p = self.savePerson(personName).then((savedPerson) => {
              savedPerson = savedPerson.data
              const opt = {
                id: savedPerson.id,
                name: personName
              }
              store.addEnumOption(opt, 'persons')
              _tryAddPerson2Roles(savedPerson.id, role, roles)
            })
            promises.push(p)
          } else {
            _tryAddPerson2Roles(existingPerson.id, role, roles)
          }
        })
      })

      Promise.all(promises).then(() => {
        self.view.record.set('roles', roles)
        self.view.runGlobalValidator()
      }).catch(store.onRequesterError.bind(store))
    }

    function _writeRating () {
      const ratingsystems = store.options.get('rating')
      const omdbdatarating = OMDBdata.Rated.split(',')

      const filmRating = overwriteExistingValues || !self.view.record.has('film_rating')
        ? []
        : [...self.view.record.get('film_rating')]

      omdbdatarating.forEach(r => {
        const region = r.split(':')[1]
        const ratingsystemsByName = ratingsystems.find(rs => rs.name === r.split(':')[0])

        if (ratingsystemsByName && !filmRating.find(r => r.region === region)) {
          filmRating.push({ rating_system_id: ratingsystemsByName.id, region })
        }
      })

      self.view.record.set('film_rating', filmRating)
    }

    transaction(() => {
      _writeAttr('common', 'name', 'Title', 'en')
      _writeAttr('common', 'shortdesc', 'Plot', 'en')
      _writeAttr('common', 'desc', 'PlotFull', 'en')
      _writeAttr('common', 'rated', 'Rated')
      _writeAttr('common', 'year', 'Year')
      _writeAttr('common', 'released', 'Released')
      _writeAttr('common', 'runtime', 'Runtime')
      _writeAttr('common', 'production', 'Production')
      _writeAttr('common', 'awards', 'Awards')
      _writeAttr('common', 'metascore', 'Metascore')
      _writeAttr('common', 'imdbRating', 'imdbRating')
      _writeAttr('common', 'imdbVotes', 'imdbVotes')
      _writeAttr('common', 'video_type', 'video_type')
      _writeAttr('common', 'website', 'Website')
      _writeAttr('common', 'boxOffice', 'BoxOffice')
      _writeAttr('common', 'imdbVotes', 'imdbVotes')
      _writeAttr('common', 'tomatoUserMeter', 'tomatoUserMeter')
      _writeAttr('common', 'tomatoUserRating', 'tomatoUserRating')
      _writeAttr('common', 'tomatoUserReviews', 'tomatoUserReviews')
      _writeAttr('common', 'tomatoURL', 'tomatoURL')
      _writeAttr('common', 'tomatoReviews', 'tomatoReviews')
      _writeAttr('common', 'tomatoFresh', 'tomatoFresh')
      _writeAttr('common', 'tomatoRotten', 'tomatoRotten')
      _writeAttr('common', 'tomatoConsensus', 'tomatoConsensus')
      _writeAttr('common', 'dvd', 'DVD')
      _overwriteTags('origcountries', 'Countries_alternative', 'Country')
      _overwriteTags('origlangs', 'OriginalVersionLanguages', 'Language')
      _overwriteTags('cats', 'Genres', 'Genre')
      _writeAttr('common', 'desc', 'Plot', 'en')
      _writeAttr('common', 'runtime', 'Runtime')
      _writePersons()
      _writeRating()
      if (overwriteExistingValues) {
        entity.set('title', OMDBdata.Title)
        if (!entity.has('id')) {
          entity.set('slug', store.toSlug(OMDBdata.Title))
        }
        if (OMDBdata.Type in TYPE_MAPPING) { // auto set of videotype
          entity.set('video_type', TYPE_MAPPING[OMDBdata.Type])
        }
      }
      !entity.has('id') && entity.set('slug', store.toSlug(OMDBdata.Title))
      this.data = false
      this.view._runValidators()
    })
  }
}
